import { useCallback, useEffect, useMemo, useState } from "react"

import * as turf from "@turf/turf"
import { isEqual } from "lodash"
import { useLocation, useMatch, useNavigate } from "react-router-dom"
import { useLocalStorage } from "react-use"

import { useMapDispatchContext } from "@l2r-front/l2r-map"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useNavigateWithSearchParams, useUrlParams } from "@l2r-front/l2r-utils"

import { NETWORK_ACCESS_AUTHORIZED, NETWORK_ACCESS_NOT_AUTHORIZED, forbiddenNetworkSlugs } from "../../constants/networks"
import { useNetwork, useNetworksQuery } from "../../hooks"

import { NetworksDispatchContext, NetworksStateContext, initialStateContext } from "./NetworksContext.context"

export const NetworksContextProvider = (props) => {
    const { children } = props

    const [networksState, setNetworksState] = useState(initialStateContext)

    const { resetMapBoundingBox, setMapPin } = useMapDispatchContext()
    const { getParam } = useUrlParams()
    const projectCache = getParam("projectCache")
    const navigate = useNavigate()
    const navigateWithSearchParams = useNavigateWithSearchParams()
    const location = useLocation()

    const { data: networks, isLoading: isLoadingNetworks, isError: isErrorNetworks } = useNetworksQuery({ projectCache: projectCache })
    const { data: selectedNetwork, isLoading: isLoadingSelectedNetwork, isError: isErrorSelectedNetwork } = useNetwork(networksState.selectedNetworkSlug, { enabled: !!networksState.selectedNetworkSlug })
    const isLoading = isLoadingNetworks || isLoadingSelectedNetwork
    const isError = isErrorNetworks || isErrorSelectedNetwork
    const [lastConsultedNetwork, setLastConsultedNetwork] = useLocalStorage("lastConsultedNetwork", null)
    const [lastConsultedSubNetworks, setLastConsultedSubNetworks] = useLocalStorage("lastConsultedSubNetworks", null)
    const [_, setLastConsultedModules] = useLocalStorage("lastConsultedModules", null)

    const match = useMatch("/:networkSlug/*")
    const matchNetworkSlug = useMemo(() => {
        return match?.params.networkSlug
    }, [match])

    const matchModuleView = useMatch("/:networkSlug/:app/:module/*")

    const allNetworks = useMemo(() => {
        if (!networks) {
            return []
        }
        const allNetworks = [...networks]
        const parentNetworks = allNetworks.filter(n => !!n.parent).map(p => p.parent)

        parentNetworks.forEach(pNetwork => {
            const parentIndex = allNetworks.findIndex(n => n.slug === pNetwork.slug)
            if (parentIndex === -1) {
                const children = allNetworks.filter(n => n.parent?.slug === pNetwork.slug)
                allNetworks.push({
                    ...children[0],
                    ...pNetwork,
                    length: null,
                    children: [...children],
                    parent: null,
                    uuid: pNetwork.uuid,
                })
            }
        })
        return allNetworks
    }, [networks])

    const setSelectedNetworkSlug = useCallback((networkSlug) => {
        setNetworksState(value => ({
            ...value,
            selectedNetworkSlug: networkSlug,
        }))
    }, [setNetworksState])

    const setSelectedSubNetworkSlugs = useCallback((subNetworkSlugs) => {
        setNetworksState(value => ({
            ...value,
            selectedSubNetworkSlugs: subNetworkSlugs,
        }))
    }, [setNetworksState])

    const setNetworkAccessStatus = useCallback((accessStatus) => {
        setNetworksState(value => ({
            ...value,
            networkAccessStatus: accessStatus,
        }))
    }, [setNetworksState])

    const setSelectedLinearLocations = useCallback((segment) => {
        const linearLocations = (segment?.properties?.linearLocation && JSON.parse(segment.properties.linearLocation)
            || segment?.linear_locations || null)
        if (!isEqual(linearLocations, networksState.selectedLinearLocations)) {
            setNetworksState(value => ({
                ...value,
                selectedLinearLocations: linearLocations,
            }))
        }
    }, [networksState, setNetworksState])

    const setRoadSearched = useCallback((road) => {
        setNetworksState(value => ({
            ...value,
            roadSearched: road,
        }))
    }, [setNetworksState])

    const swapToNetwork = useCallback((newNetworkSlug, subNetworkSlugs = null, options = {}) => {
        setMapPin()
        if (networksState.selectedNetworkSlug === newNetworkSlug && matchNetworkSlug === newNetworkSlug) {
            if (!isEqual(subNetworkSlugs, networksState.selectedSubNetworkSlugs)) {
                if (matchModuleView?.params["*"] && matchModuleView.params.app !== "campaign") {
                    navigateWithSearchParams(options.redirect ?? `/${matchModuleView.params.networkSlug}/${matchModuleView.params.app}/${matchModuleView.params.module}`)
                }
            } else {
                return
            }
        } else {
            navigate(options.redirect ?? `/${newNetworkSlug}`)
        }

        setSelectedNetworkSlug(newNetworkSlug)
        if (!subNetworkSlugs) {
            const allNewSubNetworks = allNetworks?.find(n => n.slug === newNetworkSlug)?.children?.map(sn => sn.slug) || null
            setSelectedSubNetworkSlugs(allNewSubNetworks)
        } else {
            const actualSubNetworkSlugs = subNetworkSlugs.filter(sn => allNetworks?.find(n => n.slug === newNetworkSlug)?.children?.map(c => c.slug).includes(sn))
            setSelectedSubNetworkSlugs(actualSubNetworkSlugs)
        }
        setLastConsultedSubNetworks(subNetworkSlugs)

        if (newNetworkSlug !== lastConsultedNetwork) {
            setLastConsultedNetwork(newNetworkSlug)
            setLastConsultedModules({})
            resetMapBoundingBox()
            setRoadSearched(null)
        }

    }, [
        navigate,
        navigateWithSearchParams,
        lastConsultedNetwork,
        resetMapBoundingBox,
        matchNetworkSlug,
        allNetworks,
        networksState.selectedNetworkSlug,
        networksState.selectedSubNetworkSlugs,
        matchModuleView?.params,
        setLastConsultedNetwork,
        setLastConsultedSubNetworks,
        setLastConsultedModules,
        setSelectedNetworkSlug,
        setSelectedSubNetworkSlugs,
        setRoadSearched,
        setMapPin,
    ])

    useEffect(() => {
        if (isLoading || isError || !networks || !!matchNetworkSlug && matchNetworkSlug === networksState.selectedNetworkSlug) {
            return
        } else {
            const networkSlugs = allNetworks.filter(n => !n.parent).map(p => p.slug)

            if (matchNetworkSlug && !forbiddenNetworkSlugs.includes(matchNetworkSlug)) {
                if (networkSlugs.includes(matchNetworkSlug)) {
                    swapToNetwork(matchNetworkSlug, matchNetworkSlug === lastConsultedNetwork ? lastConsultedSubNetworks : null, { redirect: location })
                    return
                }
                setNetworkAccessStatus(NETWORK_ACCESS_NOT_AUTHORIZED)
                return
            }

            setNetworkAccessStatus(NETWORK_ACCESS_AUTHORIZED)
            if (!networkSlugs.length || forbiddenNetworkSlugs.includes(matchNetworkSlug)) {
                return
            }
            if (networkSlugs.includes(lastConsultedNetwork)) {
                swapToNetwork(lastConsultedNetwork, lastConsultedSubNetworks)
                return
            }
            const firstParentNetwork = allNetworks.find(n => !n.parent)
            swapToNetwork(firstParentNetwork ? firstParentNetwork.slug : allNetworks[0].slug)
        }
    }, [isLoading,
        isError,
        networks,
        allNetworks,
        matchNetworkSlug,
        lastConsultedNetwork,
        lastConsultedSubNetworks,
        location,
        networksState.selectedNetworkSlug,
        setNetworkAccessStatus,
        swapToNetwork,
    ])

    const allSubNetworksSelected = useMemo(() => {
        return selectedNetwork?.children?.length === networksState.selectedSubNetworkSlugs?.length
    }, [selectedNetwork, networksState.selectedSubNetworkSlugs])

    const getSelectedNetworkBoundingBox = useCallback((module) => {
        if (!selectedNetwork) {
            return null
        }

        if (!networksState.selectedSubNetworkSlugs?.length) {
            return turf.bbox(selectedNetwork.boundingBox)
        } else {
            const bboxes = selectedNetwork.children.filter(sn => networksState.selectedSubNetworkSlugs.includes(sn.slug))
                ?.filter(sn => module ? sn.modules?.includes(module) : true)
                .map(sn => turf.bbox(sn.boundingBox))
            return bboxes.reduce((acc, bbox) => {
                return [
                    Math.min(acc[0], bbox[0]),
                    Math.min(acc[1], bbox[1]),
                    Math.max(acc[2], bbox[2]),
                    Math.max(acc[3], bbox[3]),
                ]
            }, [Infinity, Infinity, -Infinity, -Infinity])
        }
    }, [selectedNetwork, networksState.selectedSubNetworkSlugs])

    const dispatchValue = useMemo(() => {
        return {
            setSelectedNetworkSlug,
            setSelectedLinearLocations,
            setRoadSearched,
            swapToNetwork: swapToNetwork,
        }
    }, [setSelectedNetworkSlug, swapToNetwork, setSelectedLinearLocations, setRoadSearched])

    const stateValue = useMemo(() => {
        return {
            getSelectedNetworkBoundingBox,
            isError,
            isLoading,
            networks,
            allNetworks,
            selectedNetwork,
            selectedSubNetworkSlugs: networksState.selectedSubNetworkSlugs,
            selectedLinearLocations: networksState.selectedLinearLocations,
            networkAccessStatus: networksState.networkAccessStatus,
            roadSearched: networksState.roadSearched,
            allSubNetworksSelected,
        }
    }, [getSelectedNetworkBoundingBox,
        isLoading,
        isError,
        networks,
        allNetworks,
        selectedNetwork,
        networksState.selectedSubNetworkSlugs,
        networksState.selectedLinearLocations,
        networksState.networkAccessStatus,
        networksState.roadSearched,
        allSubNetworksSelected,
    ])

    return (
        <NetworksStateContext.Provider value={stateValue}>
            <NetworksDispatchContext.Provider value={dispatchValue}>
                {children}
            </NetworksDispatchContext.Provider>
        </NetworksStateContext.Provider>
    )
}

NetworksContextProvider.propTypes = {
    children: PropTypes.node.isRequired,
}